// frontend/src/queries/useApiMutation.js
//
// Factory wrapping useMutation with the boilerplate every list-mutation
// in the app needs:
//
//   - Announcing what the write changed (`queries/dataChanged`), once it landed
//   - Optional optimistic update with automatic rollback on error
//   - Pass-through hooks for caller-specific side-effects (showToast,
//     dialog close)
//
// The mutationFn passed in MUST throw `ApiError` on failure — use
// `apiJson.*` from queries/api.js. That way `mutation.error` is always a
// typed value and `translateRequestError(err, t, fallback)` works
// uniformly across consumers.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { assertEdits, assertKinds, useDataChanged } from './dataChanged';

/**
 * @param {object} opts
 * @param {(vars) => Promise<unknown>} opts.mutationFn
 *   The actual write — should call `apiJson.post(...)` etc. so failures
 *   throw `ApiError`. Resolved value flows through onSuccess.
 *
 * @param {string[]} opts.changes
 *   The kinds of store data the write changes (`DATA` in
 *   queries/dataChanged.js). Announced once the write landed, so every reader
 *   of those kinds reads again; a refused write announces nothing, since the
 *   server changed nothing. Required: a write that changes nothing any reader
 *   caches says so with `[]`, so a forgotten declaration cannot pass as one.
 *
 * @param {string[]} [opts.edits=[]]
 *   The grid endpoints (`GRID_READS` in queries/dataChanged.js) whose rows the
 *   write changes in place, adding and removing none. Those grids read the
 *   rows they show again by id and leave them where they stand, rather than
 *   reading their pages again in the new sort order.
 *
 * @param {boolean} [opts.awaitChanges=false]
 *   Hold `mutateAsync` (and `isPending`) until the readers on screen have
 *   been read again, for a caller that must show the server's values and
 *   nothing older once the call resolves.
 *
 * @param {object} [opts.optimistic]
 *   When provided, the cache is mutated synchronously before the network
 *   round-trip and rolled back on error.
 *   - `cacheKey`: prefix passed to cancelQueries / getQueriesData.
 *   - `apply(queryClient, vars)`: caller mutates the cache in place.
 *
 * @param {(data, vars, ctx) => void} [opts.onSuccess]
 *   Caller hook for success-only side-effects (showToast, dialog close).
 *
 * @param {(error, vars, ctx) => void} [opts.onError]
 *   Caller hook for error-only side-effects. Runs AFTER rollback so the
 *   cache is already restored when this fires.
 *
 * @param {(data, error, vars, ctx) => void|Promise<void>} [opts.onSettled]
 *   Caller hook for both-paths side-effects. Runs after the change was
 *   announced. A returned promise is awaited: `mutateAsync` resolves (and
 *   `isPending` clears) only once it settles.
 *
 * @returns {import('@tanstack/react-query').UseMutationResult}
 *   The full mutation handle: `.mutate`, `.mutateAsync`, `.isPending`,
 *   `.isError`, `.error` (typed `ApiError`), `.reset()`.
 */
export function useApiMutation({
  mutationFn,
  changes,
  edits = [],
  awaitChanges = false,
  optimistic = null,
  onSuccess,
  onError,
  onSettled,
}) {
  assertKinds(changes);
  assertEdits(edits);
  const queryClient = useQueryClient();
  const changed = useDataChanged();

  return useMutation({
    mutationFn,

    onMutate: optimistic
      ? async (vars) => {
          // Cancel in-flight refetches so they can't overwrite the
          // optimistic state mid-flight.
          await queryClient.cancelQueries({ queryKey: optimistic.cacheKey });
          // Snapshot every cached query under the prefix so rollback can
          // restore filter-variant caches that the apply() step touched.
          const previous = queryClient.getQueriesData({ queryKey: optimistic.cacheKey });
          optimistic.apply(queryClient, vars);
          return { previous };
        }
      : undefined,

    onError: (err, vars, ctx) => {
      // Rollback ALL snapshotted queries. Done before caller's onError
      // runs so any UI refresh inside onError sees the restored state.
      if (ctx?.previous) {
        ctx.previous.forEach(([key, data]) => {
          queryClient.setQueryData(key, data);
        });
      }
      onError?.(err, vars, ctx);
    },

    onSuccess: onSuccess
      ? (data, vars, ctx) => onSuccess(data, vars, ctx)
      : undefined,

    onSettled: async (data, error, vars, ctx) => {
      if (!error && changes.length > 0) {
        const reread = changed(changes, edits);
        if (awaitChanges) await reread;
      }
      return onSettled?.(data, error, vars, ctx);
    },
  });
}
